"use client"

import * as React from "react"
import { format } from "date-fns"
import { CalendarIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

interface DatePickerProps {
  date?: Date
  onDateChange: (date: Date | undefined) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  allowFutureDates?: boolean
  maxDate?: Date
  minDate?: Date
}

export function DatePicker({
  date,
  onDateChange,
  placeholder = "Pick a date",
  disabled = false,
  className,
  allowFutureDates = true,
  maxDate,
  minDate,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false)

  // Calculate start and end months based on constraints
  const startMonth = minDate ? new Date(minDate.getFullYear(), minDate.getMonth()) : new Date(1903, 11) // December 1903
  const endMonth = maxDate ? new Date(maxDate.getFullYear(), maxDate.getMonth()) : new Date(new Date().getFullYear() + 25, 11) // 25 years from now

  // Use selected date as default month, or current month if no date selected
  const defaultMonth = date || new Date()

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !date && "text-muted-foreground",
            className
          )}
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? format(date, "PPP") : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          defaultMonth={defaultMonth}
          captionLayout="dropdown"
          startMonth={startMonth}
          endMonth={endMonth}
          disabled={(date) => {
            if (minDate && date < minDate) return true
            if (maxDate && date > maxDate) return true
            if (!allowFutureDates && date > new Date()) return true
            return false
          }}
          onSelect={(selectedDate: Date | undefined) => {
            onDateChange(selectedDate)
            setOpen(false)
          }}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  )
}

// Form-integrated version for use with react-hook-form
interface DatePickerFormFieldProps {
  value?: Date
  onChange: (date: Date | undefined) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  allowFutureDates?: boolean
  maxDate?: Date
  minDate?: Date
}

export function DatePickerFormField({
  value,
  onChange,
  placeholder = "Pick a date",
  disabled = false,
  className,
  allowFutureDates = true,
  maxDate,
  minDate,
}: DatePickerFormFieldProps) {
  const [open, setOpen] = React.useState(false)

  // Calculate start and end months based on constraints
  const startMonth = minDate ? new Date(minDate.getFullYear(), minDate.getMonth()) : new Date(1903, 11) // December 1903
  const endMonth = maxDate ? new Date(maxDate.getFullYear(), maxDate.getMonth()) : new Date(new Date().getFullYear() + 25, 11) // 25 years from now

  // Use selected date as default month, or current month if no date selected
  const defaultMonth = value || new Date()

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !value && "text-muted-foreground",
            className
          )}
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? format(value, "PPP") : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          defaultMonth={defaultMonth}
          captionLayout="dropdown"
          startMonth={startMonth}
          endMonth={endMonth}
          disabled={(date) => {
            if (minDate && date < minDate) return true
            if (maxDate && date > maxDate) return true
            if (!allowFutureDates && date > new Date()) return true
            return false
          }}
          onSelect={(selectedDate: Date | undefined) => {
            onChange(selectedDate)
            setOpen(false)
          }}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  )
} 